// Rig Data Sheet — v2 foundation schema.
//
// This module is intentionally NOT colocated with the legacy six-profile
// schemas in /lib/profiles. It is the first foundational module of the
// Kill Sheet v2 rebuild and must remain extractable from the legacy
// workflow.
//
// Storage shape: see /lib/rigsheet/storage.js (key: rsc.rigsheets.v1).
// Per-pump derived values: see /lib/rigsheet/compute.js.

export const RIG_TYPES = ["Land", "Jackup", "Semi", "Drillship"];
export const PUMP_TYPES_V2 = ["triplex", "duplex"];
export const PUMP_TYPE_LABELS_V2 = {
  triplex: "Triplex",
  duplex:  "Duplex",
};
export const UNIT_SYSTEMS = ["IMP", "SI"];

// Rig types that emphasise the Subsea section in the UI. Land hides it
// (still in schema, just visually de-emphasised). Schema is identical
// across rig types so the user can fill subsea data on any rig if needed.
export const SUBSEA_EMPHASISED_TYPES = new Set(["Jackup", "Semi", "Drillship"]);

const _newPumpId = () =>
  `pump_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export function emptyPump(label = "Pump 1") {
  return {
    id: _newPumpId(),
    label,
    pumpType: "triplex",
    linerSize: "",
    strokeLength: "",
    rodDiameter: "",
    efficiency: "97",
    notes: "",
    computed: {
      dispBblPerStk: "",
      dispM3PerStk:  "",
      strokesPerBbl: "",
      strokesPerM3:  "",
    },
  };
}

export function emptyRigSheet() {
  return {
    // Section 1 — Identity
    rigName:        "",
    rigType:        "Land",
    operator:       "",
    rigContractor:  "",
    // Separate, authoritative Field and Block — these are the 1:1 match for
    // the Well Profile's own `field` / `block` and are what the RCE derivers
    // publish as rig.field / rig.block. `fieldOrBlock` below is the legacy
    // combined free-text value; it is kept so existing sheets keep rendering
    // and stay editable, but nothing derives from it (splitting one string
    // into two fields would be a guess, not a deterministic relationship).
    field:          "",
    block:          "",
    fieldOrBlock:   "",
    country:        "",
    units:          "IMP",
    // Module 3.2a bridge — optional link to a Well Profile (master record).
    // When set, identity fields are derived from the well on the Editor side.
    linkedWellId:   "",

    // Section 2 — Mud Pumps (1..N)
    pumps: [emptyPump("Pump 1")],

    // Section 3 — Surface Line geometry (supporting only; no canonical volume here)
    surfaceLine: {
      standpipeId:      "",
      standpipeLength:  "",
      rotaryHoseId:     "",
      rotaryHoseLength: "",
    },

    // Section 4 — Well Control Volumes (CANONICAL source-of-truth for kill sheets)
    wellControlVolumes: {
      surfaceLineVolume: "",
      riserVolume:       "",
      chokeLineVolume:   "",
      killLineVolume:    "",
      bopLmrpVolume:     "",
    },

    // Section 5 — Surface BOP Stack
    surfaceBop: {
      present:                 true,
      bopRefId:                "",
      annular:                 "",
      pipeRams:                "",
      blindRams:               "",
      shearRams:               "",
      annularWorkingPressure:  "",
      ramWorkingPressure:      "",
    },

    // Section 6 — Subsea / Marine geometry (IDs + depths; volumes live in Section 4)
    subsea: {
      waterDepth:    "",
      riserId:       "",
      chokeLineId:   "",
      killLineId:    "",
    },

    // Section 7 — Choke Manifold
    chokeManifold: {
      label:           "",
      type:            "",
      numberOfChokes: "",
      notes:           "",
    },

    // Section 8 — Rig Defaults & Notes
    defaults: {
      defaultCirculatingPumpId: "",
      defaultKillPumpId:        "",
      rigNotes:                 "",
    },
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const _isNum    = (v) => v !== "" && v != null && Number.isFinite(Number(v));
const _isPosNum = (v) => _isNum(v) && Number(v) > 0;

function _isOptionalNum(v) {
  // Empty string / null = not entered = OK. Otherwise must parse as number.
  if (v === "" || v == null) return true;
  return _isNum(v);
}
function _isOptionalPosNum(v) {
  if (v === "" || v == null) return true;
  return _isPosNum(v);
}

/**
 * Validate a Rig Data Sheet payload. Returns { ok, errors }.
 * Severity model (per brief Section 11, approved as proposed):
 *   - block-save errors        : identity required-fields, per-pump core geometry
 *   - numeric-only checks      : optional volumes/pressures must parse if entered
 * Informational notices (e.g. Drillship with no subsea data) are NOT returned
 * here; the UI surfaces those non-blocking hints separately.
 */
export function validateRigSheet(p) {
  const errs = [];
  if (!p || typeof p !== "object") return { ok: false, errors: ["RigSheet payload is missing."] };

  // Section 1
  if (!p.rigName || !String(p.rigName).trim())
    errs.push("Rig Name is required.");
  if (!RIG_TYPES.includes(p.rigType))
    errs.push("Rig Type must be one of: " + RIG_TYPES.join(", ") + ".");
  if (!UNIT_SYSTEMS.includes(p.units))
    errs.push("Default Unit System must be IMP or SI.");

  // Section 2 — Pumps
  if (!Array.isArray(p.pumps) || p.pumps.length === 0) {
    errs.push("At least one mud pump must be defined.");
  } else {
    p.pumps.forEach((pump, i) => {
      const tag = `Pump #${i + 1}`;
      if (!PUMP_TYPES_V2.includes(pump.pumpType))
        errs.push(`${tag}: Pump Type must be Triplex or Duplex.`);
      if (!_isPosNum(pump.linerSize))
        errs.push(`${tag}: Liner Size must be a positive number.`);
      if (!_isPosNum(pump.strokeLength))
        errs.push(`${tag}: Stroke Length must be a positive number.`);
      if (!_isNum(pump.efficiency))
        errs.push(`${tag}: Efficiency must be a number.`);
      else if (Number(pump.efficiency) <= 0 || Number(pump.efficiency) > 100)
        errs.push(`${tag}: Efficiency must be greater than 0 and ≤ 100.`);
      if (pump.pumpType === "duplex" && !_isPosNum(pump.rodDiameter))
        errs.push(`${tag}: Rod Diameter is required for Duplex pumps and must be positive.`);
    });
    // Pump label uniqueness — soft constraint to avoid downstream confusion.
    const seen = new Set();
    p.pumps.forEach((pump, i) => {
      const lbl = String(pump.label || "").trim().toLowerCase();
      if (lbl && seen.has(lbl))
        errs.push(`Pump #${i + 1}: duplicate label '${pump.label}'.`);
      if (lbl) seen.add(lbl);
    });
  }

  // Section 3 — Surface Line geometry (all optional, numeric if entered)
  if (p.surfaceLine) {
    if (!_isOptionalPosNum(p.surfaceLine.standpipeId))
      errs.push("Standpipe ID must be a positive number if entered.");
    if (!_isOptionalPosNum(p.surfaceLine.standpipeLength))
      errs.push("Standpipe Length must be a positive number if entered.");
    if (!_isOptionalPosNum(p.surfaceLine.rotaryHoseId))
      errs.push("Rotary Hose ID must be a positive number if entered.");
    if (!_isOptionalPosNum(p.surfaceLine.rotaryHoseLength))
      errs.push("Rotary Hose Length must be a positive number if entered.");
  }

  // Section 4 — Well Control Volumes (canonical; all optional but must be numeric if entered)
  if (p.wellControlVolumes) {
    [
      ["surfaceLineVolume", "Surface Line Volume"],
      ["riserVolume",       "Marine Riser Volume"],
      ["chokeLineVolume",   "Choke Line Volume"],
      ["killLineVolume",    "Kill Line Volume"],
      ["bopLmrpVolume",     "BOP / LMRP Volume"],
    ].forEach(([key, label]) => {
      if (!_isOptionalPosNum(p.wellControlVolumes[key]))
        errs.push(`${label} must be a positive number if entered.`);
    });
  }

  // Section 5 — Surface BOP
  if (p.surfaceBop) {
    if (!_isOptionalPosNum(p.surfaceBop.annularWorkingPressure))
      errs.push("Annular Working Pressure must be a positive number if entered.");
    if (!_isOptionalPosNum(p.surfaceBop.ramWorkingPressure))
      errs.push("Ram Working Pressure must be a positive number if entered.");
  }

  // Section 6 — Subsea geometry
  if (p.subsea) {
    if (!_isOptionalPosNum(p.subsea.waterDepth))
      errs.push("Water Depth must be a positive number if entered.");
    if (!_isOptionalPosNum(p.subsea.riserId))
      errs.push("Marine Riser ID must be a positive number if entered.");
    if (!_isOptionalPosNum(p.subsea.chokeLineId))
      errs.push("Choke Line ID must be a positive number if entered.");
    if (!_isOptionalPosNum(p.subsea.killLineId))
      errs.push("Kill Line ID must be a positive number if entered.");
  }

  // Section 7 — Choke Manifold
  if (p.chokeManifold) {
    if (p.chokeManifold.numberOfChokes !== "" && p.chokeManifold.numberOfChokes != null) {
      const n = Number(p.chokeManifold.numberOfChokes);
      if (!Number.isInteger(n) || n < 0)
        errs.push("Number of chokes must be a non-negative integer.");
    }
  }

  // Section 8 — Defaults
  if (p.defaults && Array.isArray(p.pumps)) {
    const pumpIds = new Set(p.pumps.map((x) => x.id));
    if (p.defaults.defaultCirculatingPumpId && !pumpIds.has(p.defaults.defaultCirculatingPumpId))
      errs.push("Default Circulating Pump references an unknown pump.");
    if (p.defaults.defaultKillPumpId && !pumpIds.has(p.defaults.defaultKillPumpId))
      errs.push("Default Kill Pump references an unknown pump.");
  }

  return { ok: errs.length === 0, errors: errs };
}

// Informational notices — non-blocking. Used by the editor to surface soft
// hints. Never affects save.
export function rigSheetSoftNotices(p) {
  const notes = [];
  if (!p) return notes;
  const subseaEmphasised = SUBSEA_EMPHASISED_TYPES.has(p.rigType);
  const subseaVolumesFilled = !!(p.wellControlVolumes && (
    p.wellControlVolumes.riserVolume || p.wellControlVolumes.chokeLineVolume ||
    p.wellControlVolumes.killLineVolume || p.wellControlVolumes.bopLmrpVolume
  ));
  if (subseaEmphasised && !subseaVolumesFilled)
    notes.push("This rig type usually has subsea well-control volumes. Section 4 is empty.");
  if (!subseaEmphasised && subseaVolumesFilled)
    notes.push("Subsea volumes are set, but the rig type is Land. Confirm this is intentional.");
  return notes;
}
